import type { SiteOrderEvent, SiteOrderView, SiteSetupView } from '@/types/api';
import type { StatusPresentation } from '@/utils/labels';

/** A new site ordered in one go (ADR-0059), in the owner's words. */

export type OrderStepState = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

export interface OrderStep {
  readonly label: string;
  readonly state: OrderStepState;
  readonly detail?: string;
}

/**
 * A new site's creation as a checklist (ADR-0047): the job's steps grouped
 * into what a person recognises — the domain and server, WordPress, the
 * template's structure, DNS, the final check.
 */
const SETUP_GROUPS: readonly { label: string; steps: readonly string[] }[] = [
  { label: 'Домен и сервер', steps: ['claim-domains', 'select-node'] },
  { label: 'База данных', steps: ['provision-database'] },
  { label: 'Конфигурация и сертификат', steps: ['publish-specification', 'issue-origin-certificate'] },
  { label: 'Запуск сайта', steps: ['start-runtime'] },
  { label: 'Установка WordPress', steps: ['install-engine'] },
  { label: 'Язык, тема и структура', steps: ['apply-structure'] },
  { label: 'DNS', steps: ['bind-domains'] },
  { label: 'Проверка', steps: ['schedule-backups', 'verify-and-publish'] },
];

export function setupGroups(setup: SiteSetupView | null | undefined): OrderStep[] {
  const byName = new Map((setup?.steps ?? []).map((step) => [step.name, step.state]));
  const finished = setup?.state === 'completed';
  return SETUP_GROUPS.map(({ label, steps }) => {
    const states = steps.map((name) => (finished ? 'done' : (byName.get(name) ?? 'pending')));
    const state: OrderStepState = states.includes('failed')
      ? 'failed'
      : states.every((item) => item === 'done')
        ? 'done'
        : states.some((item) => item !== 'pending')
          ? 'running'
          : 'pending';
    return { label, state };
  });
}

const ORDER_PHASES = ['paying', 'purchasing', 'awaiting_scope', 'creating', 'done'] as const;

/** How far an order got before it stopped: the phase it failed in. */
function failedPhase(view: SiteOrderView): (typeof ORDER_PHASES)[number] {
  if (view.siteId) return 'creating';
  if (view.purchasedAt) return 'awaiting_scope';
  if (view.events.some((event) => event.code === 'paid')) return 'purchasing';
  return 'paying';
}

/**
 * Every step of the order as one list: paying, buying, the zone and the
 * domain handed to the panel — for a bought domain — then the site's own
 * creation step by step. The step it stopped at is marked failed.
 */
export function orderSteps(view: SiteOrderView): OrderStep[] {
  const phase = view.state === 'failed' ? failedPhase(view) : view.state;
  const at = ORDER_PHASES.indexOf(phase);
  const stateOf = (index: number): OrderStepState => {
    if (index < at) return 'done';
    if (index > at) return 'pending';
    return view.state === 'failed' ? 'failed' : view.state === 'done' ? 'done' : 'running';
  };

  const steps: OrderStep[] = [];
  if (view.buying) {
    const provisioning = view.state === 'purchasing' && view.purchase?.status === 'PROVISIONING_DNS';
    const unknown = view.state === 'paying' && view.events.some((event) => event.code === 'payment_unknown');
    steps.push(
      { label: 'Оплата домена', state: stateOf(0), ...(unknown ? { detail: 'Запрос отправлен, ждём подтверждения 2d2s' } : view.cost !== null ? { detail: formatOrderMoney(view.cost, view.currency) } : {}) },
      { label: 'Покупка у регистратора', state: provisioning ? 'done' : stateOf(1) },
      { label: 'Зона Cloudflare и NS', state: provisioning ? 'running' : stateOf(1) === 'done' ? 'done' : 'pending' },
      {
        label: 'Домен доступен панели',
        state: stateOf(2),
        ...(view.state === 'awaiting_scope' && view.waitingFor
          ? { detail: view.waitingFor === 'tag' ? `Ждём тег ${view.writeTags.map((tag) => `«${tag}»`).join(', ') || 'панели'} в 2d2s` : 'Ждём зону Cloudflare' }
          : {}),
      },
    );
  }

  const setup = setupGroups(view.setup);
  if (at < 3) return [...steps, ...setup.map((step) => ({ ...step, state: 'pending' as const }))];
  if (view.state === 'failed' && phase === 'creating' && !setup.some((step) => step.state === 'failed')) {
    // The job failed before it said where: the first step not done is the one.
    const first = setup.findIndex((step) => step.state !== 'done');
    if (first >= 0) setup[first] = { ...setup[first]!, state: 'failed' };
  }
  return [...steps, ...setup];
}

/** The order's state as a pill: where it is, in a word. */
export const SITE_ORDER_STATE: Record<string, StatusPresentation> = {
  paying: { tone: 'pending', label: 'Оплата', glyph: '○', hint: 'Оплата домена в 2d2s' },
  purchasing: { tone: 'pending', label: 'Покупка домена', glyph: '○', hint: '2d2s покупает домен и делает зону Cloudflare' },
  awaiting_scope: { tone: 'pending', label: 'Подготовка домена', glyph: '○', hint: 'Домен куплен, панель ждёт доступа к его DNS' },
  creating: { tone: 'pending', label: 'Создание сайта', glyph: '○', hint: 'Сервер, WordPress, шаблон и DNS' },
  done: { tone: 'healthy', label: 'Готов', glyph: '●', hint: 'Сайт работает, закрыт от поисковиков до проверки' },
  failed: { tone: 'down', label: 'Не создан', glyph: '■', hint: 'Причина — в окне заказа' },
};

/** Why an order stopped, and what to do, in the owner's words. */
export function orderFailureText(view: SiteOrderView): string {
  const tags = view.writeTags.map((tag) => `«${tag}»`).join(', ');
  switch (view.errorCode) {
    case 'PAYMENT_UNKNOWN':
      return 'Оплата не подтверждена 2d2s за 15 минут. Повторно оплата не отправлялась — проверьте покупку в 2d2s.';
    case 'PAYMENT_NOT_SENT':
      return 'Оплата не была отправлена, домен не куплен.';
    case 'PROVIDER_REJECTED':
      return `2d2s отказал в оплате${view.error ? `: ${view.error.replace(/^2d2s:\s*/, '')}` : ''}.`;
    case 'PURCHASE_CANCELLED':
      return 'Покупка домена отменена в 2d2s.';
    case 'PURCHASE_FAILED':
      return `Регистратор не купил домен${view.error ? `: ${view.error}` : ''}.`;
    case 'PURCHASE_TIMEOUT':
      return '2d2s покупает домен больше суток. Проверьте покупку в 2d2s.';
    case 'SCOPE_TIMEOUT':
      return view.error?.includes('tags')
        ? `Домен не получил ${tags ? `тег ${tags}` : 'тег панели'} в 2d2s за 2 часа. Добавьте тег в 2d2s и создайте сайт из «Моих доменов».`
        : 'Зона Cloudflare домена не появилась за 2 часа. Проверьте домен в 2d2s.';
    case 'DOMAIN_ALREADY_BOUND':
      return 'Домен уже привязан к другому сайту.';
    case 'VALIDATION_FAILED':
      return 'У шаблона больше нет готовой сборки.';
    default:
      return view.siteId ? `Создание сайта остановлено (${view.errorCode ?? 'ошибка'}). Подробности — на странице сайта.` : `Заказ остановлен: ${view.errorCode ?? 'ошибка'}.`;
  }
}

/** Whether the domain was bought before the order stopped: then it stays in 2d2s. */
export function orderBoughtDomain(view: SiteOrderView): boolean {
  return view.buying && (view.purchasedAt !== null || view.events.some((event) => event.code === 'paid'));
}

export function formatOrderMoney(amount: number | null, currency: string | null): string {
  if (amount === null) return 'цена не сообщается';
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: currency ?? 'USD', maximumFractionDigits: 2 }).format(amount);
}

/** A line of the order's history. */
export function orderEventText(event: SiteOrderEvent, writeTags: readonly string[]): string {
  switch (event.code) {
    case 'created':
      return `Заказ создан${event.by ? ` · ${event.by}` : ''}`;
    case 'paid':
      return `Домен оплачен${event.cost !== undefined ? ` · ${formatOrderMoney(event.cost ?? null, event.currency ?? null)}` : ''}`;
    case 'payment_unknown':
      return 'Оплата отправлена, ответ 2d2s не получен — повторной оплаты не будет';
    case 'purchased':
      return 'Домен куплен, зона Cloudflare готова';
    case 'waiting':
      return event.waitingFor === 'tag' ? `Ждём, когда домен получит тег ${writeTags.map((tag) => `«${tag}»`).join(', ') || 'панели'} в 2d2s` : 'Ждём зону Cloudflare домена';
    case 'domain_ready':
      return 'Домен доступен панели';
    case 'site_queued':
      return 'Создание сайта запущено';
    case 'done':
      return 'Сайт готов';
    case 'failed':
      return `Заказ остановлен${event.errorCode ? `: ${event.errorCode}` : ''}`;
  }
}
